import React from 'react';
import { createPortal } from 'react-dom';
import { Modal, View, StyleSheet, Platform } from 'react-native';

export const MAP_STAGE_CLASS = 'vrc-map-stage';
export const MAP_STAGE_FS_ATTR = 'data-map-fullscreen';

/**
 * Resolve the Tour Planner map stage element when it is in browser
 * fullscreen or CSS/app fullscreen (`data-map-fullscreen="true"`).
 */
export function resolveMapFullscreenHost(explicitHost = null) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return null;

  const stageFrom = (el) => {
    if (!el) return null;
    if (el.classList?.contains?.(MAP_STAGE_CLASS)) return el;
    return el.closest?.(`.${MAP_STAGE_CLASS}`) || null;
  };

  if (explicitHost) {
    const stage = stageFrom(explicitHost) || explicitHost;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    const marked = stage?.getAttribute?.(MAP_STAGE_FS_ATTR) === 'true';
    const inFs = Boolean(
      fsEl === stage
      || (fsEl && stage?.contains?.(fsEl))
      || marked
    );
    return inFs ? stage : null;
  }

  const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
  const fromFs = stageFrom(fsEl);
  if (fromFs) return fromFs;

  return document.querySelector(`.${MAP_STAGE_CLASS}[${MAP_STAGE_FS_ATTR}="true"]`);
}

/**
 * Drop-in Modal replacement: when the Tour Planner map is fullscreen,
 * content is portaled into the map stage so it remains visible.
 */
export default function FullscreenSafeModal({
  visible = false,
  onRequestClose,
  children,
  animationType = 'fade',
  transparent = true,
  hostRef = null,
  /** Extra style applied only when portaled into the map stage */
  hostOverlayStyle = null,
  statusBarTranslucent,
  ...rest
}) {
  if (!visible) return null;

  const explicit = hostRef?.current || null;
  const host = resolveMapFullscreenHost(explicit);

  if (host) {
    return createPortal(
      <View
        style={[styles.hostLayer, hostOverlayStyle]}
        pointerEvents="box-none"
      >
        {children}
      </View>,
      host,
    );
  }

  return (
    <Modal
      visible
      transparent={transparent}
      animationType={animationType}
      onRequestClose={onRequestClose}
      statusBarTranslucent={statusBarTranslucent}
      {...rest}
    >
      {children}
    </Modal>
  );
}

const styles = StyleSheet.create({
  hostLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    zIndex: 2147483646,
    // Let Modal children that use flex:1 fill the stage
    display: 'flex',
    flexDirection: 'column',
  },
});
